import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { formatPhoneNumber } from 'react-phone-number-input'
import { useDispatch, useSelector } from 'react-redux'
import writtenNumber from 'written-number'

import Button from '@/components-latest/primitives/Button'
import Separator from '@/components-latest/primitives/Separator'
import Spinner from '@/components-latest/primitives/Spinner'
import Text from '@/components-latest/primitives/Text'
import Accordion from '@/components-latest/shared/Accordion'
import CommonModal from '@/components-latest/shared/Modals/CommonModal'
import { SupportedLanguages } from '@/config/i18n'
import { IBank } from '@/ducks/billing/billing.types'
import { addSystemNotification } from '@/ducks/notifications/slices/notification.slice'
import {
  selectCurrencyCode,
  selectFullPhone
} from '@/ducks/store/selectors/store.selectors'
import { capitalize } from '@/utils/string.utils'

import Styles from './BankInformationModal.module.scss'

const BankInfoRow: React.FC<{ label: string; value: string | number }> = ({
  label,
  value
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(String(value))
    dispatch(
      addSystemNotification({
        message: t('billing.bank-information.copy-notification', {
          label
        }),
        intent: 'info',
        duration: 2000
      })
    )
  }, [dispatch, label, t, value])

  return (
    <div className={Styles.bankInfoRow}>
      <div>
        <Text
          variant="nextBody"
          weight="regular"
          gutter={8}
          className={Styles.rowLabel}>
          {label}
        </Text>
        <Text
          variant="nextSubtitle"
          weight="regular"
          className={Styles.rowValue}>
          {value}
        </Text>
      </div>
      <div>
        <Button variant="tertiary" size="smaller" onClick={handleCopy}>
          {t('billing.bank-information.copy')}
        </Button>
      </div>
    </div>
  )
}

interface IBankInformationModalProps {
  children: React.ReactNode
  final_admin_earnings: number
  banks?: Array<IBank>
}

const BankInformationModal: React.FC<IBankInformationModalProps> = ({
  children,
  final_admin_earnings,
  banks
}) => {
  const { t, i18n } = useTranslation()

  const language = (i18n.resolvedLanguage || 'en') as SupportedLanguages

  const currency = useSelector(selectCurrencyCode)
  const phone = useSelector(selectFullPhone) || ''

  const [isOpen, setIsOpen] = useState(false)

  const body = (
    <div className={Styles.content}>
      <Text variant="nextSubtitle" weight="regular">
        {t('billing.bank-information.description')}
      </Text>
      {banks ? (
        banks.map(bank => (
          <div className={Styles.accordionWrapper} key={bank.name}>
            <Accordion label={bank.name} variant="simple">
              <div className={Styles.bankRows}>
                <Separator />

                <BankInfoRow
                  label={t('billing.bank-information.name')}
                  value={bank.account_holder}
                />
                <BankInfoRow
                  label={t('billing.bank-information.number')}
                  value={bank.account_number}
                />

                <Separator />

                <BankInfoRow
                  label={t('billing.bank-information.phone')}
                  value={formatPhoneNumber(phone)}
                />
                <BankInfoRow
                  label={t('billing.bank-information.price', { currency })}
                  value={new Intl.NumberFormat('fr-FR', {
                    useGrouping: true
                  }).format(final_admin_earnings)}
                />
                <BankInfoRow
                  label={t('billing.bank-information.price-in-letter')}
                  value={capitalize(
                    writtenNumber(final_admin_earnings, { lang: language })
                  )}
                />
              </div>
            </Accordion>
          </div>
        ))
      ) : (
        <div className={Styles.spinner}>
          <Spinner />
        </div>
      )}

      <div className={Styles.infoBlock}>
        <Text variant="nextCaption" weight="regular">
          It usually takes a couple of days for the transfer to be completed.
        </Text>
      </div>
    </div>
  )

  return (
    <div>
      <CommonModal
        scrollableBody
        className={Styles.modal}
        scrollAreaClass={Styles.scrollArea}
        bodyClass={Styles.body}
        body={body}
        open={isOpen}
        title={t('billing.bank-information.title')}
        onOpenChange={open => setIsOpen(open)}>
        {children}
      </CommonModal>
    </div>
  )
}

export default BankInformationModal
